import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const AnswerQueryModal: React.FC<AnswerQueryModalProps> = ({
  isOpen,
  onClose,
  question,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useTranslation("common");
  const [answer, setAnswer] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isFormValid = answer.trim().length > 0;

  useEffect(() => {
    if (isOpen) {
      setAnswer("");
      setIsSubmitted(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setIsSubmitted(true);

    if (!isFormValid || !question?.id) return;

    try {
      await onSubmit(answer);
      handleResetAndClose();
    } catch (error) {
      console.error("Error submitting answer:", error);
    }
  };

  const handleResetAndClose = () => {
    setAnswer("");
    setIsSubmitted(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleResetAndClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p={6} bg="#FFFFFF">
        <ModalHeader fontSize="24px" fontWeight="semibold" color="#2F2F34">
          {t("faq.answerQuery")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {question?.title && (
            <Text mb={2} color="#2F2F34" fontSize="16px" fontWeight="semibold">
              {question?.title}
            </Text>
          )}
          {question?.content && (
            <Text mb={2} color="#2F2F34" fontSize="16px" fontWeight="semibold">
              {question?.content}
            </Text>
          )}
          <VStack spacing={6}>
            <FormControl isInvalid={isSubmitted && !isFormValid}>
              <FormLabel fontSize="16px" fontWeight="semibold" color="#2F2F34">
                {t("faq.answerLabel")}
              </FormLabel>
              <Textarea
                fontSize="14px"
                fontWeight="medium"
                color="#2F2F34"
                placeholder={t("faq.answerPlaceholder")}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={5}
              />
              <FormErrorMessage>{t("faq.answerError")}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={4}>
          <Button
            onClick={handleResetAndClose}
            bg="#FFFFFF"
            fontWeight="500"
            fontSize="14px"
            h="44px"
            w="87px"
            color="#2F2F34"
            borderColor="#FFFFFF"
          >
            {t("faq.cancelButton")}
          </Button>
          <Button
            bg="#1819B4"
            color="#FFFFFF"
            fontSize="14px"
            fontWeight="500"
            h="44px"
            w="143px"
            borderRadius="8px"
            maxW="fit-content"
            onClick={handleSubmit}
            _hover={{ bg: "#0f108c" }}
            isDisabled={!isFormValid || isLoading}
          >
            {isLoading ? <Spinner size="sm" /> : t("faq.submitAnswerButton")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AnswerQueryModal;
